import { useMemo } from 'react'
import { FtIcon } from './icons.tsx'
import { FreeTalkStore, Message, formatClock } from '../freeTalkStore.ts'

function hashStr(s: string) {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function useBars(id: number, durationSec: number) {
  return useMemo(() => {
    const n = Math.max(14, Math.min(34, Math.round(durationSec * 2.6)))
    let seed = hashStr(String(id)) % 233280
    const out: number[] = []
    for (let i = 0; i < n; i++) {
      seed = (seed * 9301 + 49297) % 233280
      const r = seed / 233280
      const env = 0.55 + 0.45 * Math.sin((Math.PI * (i + 0.5)) / n)
      out.push(Math.round(5 + 21 * env * (0.35 + 0.65 * r)))
    }
    return out
    // Bars are fixed once the message exists; the duration refines them only before the first paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])
}

export function MessageBubble(props: {
  m: Message
  store: FreeTalkStore
  playing: boolean
  progress: number
  disabled: boolean
}) {
  const { m, store, playing, progress } = props
  const bars = useBars(m.id, m.durationSec)

  if (m.kind === 'typing') {
    return (
      <div className="ft-msg ai ft-typing" aria-label="Собеседник отвечает">
        <div className="ft-bubble">
          <i />
          <i />
          <i />
        </div>
      </div>
    )
  }
  if (m.kind === 'error') {
    return (
      <div className="ft-syserr">
        <FtIcon name="warn" size={18} />
        Не получилось ответить
        <button
          onClick={() => {
            store.retry(m.id)
          }}
        >
          Повторить
        </button>
      </div>
    )
  }

  const on = Math.round(progress * bars.length)
  const remaining = playing ? m.durationSec * (1 - progress) : m.durationSec

  return (
    <div className={`ft-msg ${m.who} ${m.status !== 'ok' ? m.status : ''}`}>
      <div className="ft-bubble">
        <button
          className="ft-play"
          aria-label={playing ? 'Пауза' : 'Прослушать'}
          disabled={props.disabled}
          onClick={() => void store.play(m.id, false)}
        >
          <FtIcon name={playing ? 'pause' : 'play'} size={16} />
        </button>
        <div className="ft-wave" aria-hidden="true">
          {bars.map((h, i) => (
            <i
              key={i}
              className={playing && i < on ? 'on' : ''}
              style={{ height: h }}
            />
          ))}
        </div>
        <span className="ft-dur">{formatClock(Math.ceil(remaining))}</span>
      </div>

      {m.status === 'failed' && (
        <div className="ft-note">
          <FtIcon name="warn" size={14} />
          Не отправлено ·
          <button
            onClick={() => {
              store.resend(m.id)
            }}
          >
            Отправить ещё раз
          </button>
        </div>
      )}
      {m.status === 'unrec' && (
        <div className="ft-note">
          <FtIcon name="warn" size={14} />
          Не получилось распознать ·
          <button
            onClick={() => {
              store.rerecord(m.id)
            }}
          >
            Записать заново
          </button>
        </div>
      )}
      {m.status === 'ok' && m.text && (
        <>
          <button
            className="ft-txtlink"
            onClick={() => {
              store.toggleText(m.id)
            }}
          >
            {m.textOpen ? 'Скрыть текст' : 'Показать текст'}
          </button>
          {m.textOpen && (
            <div className="ft-txt">
              <span lang="en">{m.text}</span>
              {m.who === 'ai' && (
                <>
                  <div className="ft-trrow">
                    <button
                      className="ft-txtlink"
                      onClick={() => {
                        store.toggleTranslation(m.id)
                      }}
                    >
                      {m.trOpen ? 'Скрыть перевод' : 'Перевести'}
                    </button>
                  </div>
                  {m.trOpen && (
                    <div className="ft-tr">
                      {m.translating ? 'Переводим…' : m.translation || '—'}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
